import { useEffect, useMemo, useState } from 'react'
import { orderBy, values } from 'lodash'
import { Loader } from 'semantic-ui-react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  selectFinancialAccounts,
  selectPlaidItems,
} from '@/selectors/financeSelectors'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import {
  getCurrentUser,
  selectRolledOutInstitutionIds,
} from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchRolledOutInstitutionIds } from '@/actions/userActions'
import { getAccountsAwaitingConfirmation } from '../../helpers'

import { TextColors, TypographyStyles } from '@/styles'
import { Card, Icon, List, Text } from '@/ui'
import { InstitutionCard } from './institution-card'
import { AccountBannerCard, AccountBannerProps } from './AccountBannerCard'
import { InitialStateDialog } from './InitialStateDialog'
import { AwaitingConfirmationButton } from './ConfirmationButton'

export const InstitutionList = ({ isOnboarding = false }) => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const plaidInstitutions = useReselector(selectPlaidItems)
  const financialAccounts = useReselector(selectFinancialAccounts)
  const rolledOutInstitutionIds = useReselector(selectRolledOutInstitutionIds)
  const [fetching, setFetching] = useState(true)
  const [initialStateModalOpen, setInitialStateModalOpen] = useState(false)

  const [accountBanner, setAccountBanner] = useState<AccountBannerProps | null>(
    null
  )

  const [accountToEdit, setAccountToEdit] = useState<FinancialAccount | null>(
    null
  )

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
      setFetching(false)
    }

    fetch()
  }, [dispatch])

  useEffect(() => {
    const fetch = async () => {
      if (user) await dispatch(fetchRolledOutInstitutionIds(user.id))
    }
    fetch()
  }, [dispatch, user, plaidInstitutions])

  const sortedPlaidInstitutions = useMemo(() => {
    if (!plaidInstitutions) return null
    return orderBy(Object.values(plaidInstitutions), 'disconnectedOn')
  }, [plaidInstitutions])

  const accountsAwaitingConfirmation = getAccountsAwaitingConfirmation(
    values(financialAccounts),
    rolledOutInstitutionIds ?? []
  )

  const hasAccountsAwaitingConfirmation = Boolean(
    rolledOutInstitutionIds?.length && accountsAwaitingConfirmation?.length
  )

  if (fetching || !user) {
    return <Loader inverted>Loading</Loader>
  }

  if (sortedPlaidInstitutions && sortedPlaidInstitutions.length > 0) {
    return (
      <div data-testid="institution-list">
        {accountBanner && <AccountBannerCard {...accountBanner} />}

        {hasAccountsAwaitingConfirmation && (
          <Card>
            <Text variant={TypographyStyles.H3}>Review bank account</Text>

            <Text
              color={TextColors.EMPHASIS}
              variant={TypographyStyles.EYEBROW}
            >
              {accountsAwaitingConfirmation.length} account
              {accountsAwaitingConfirmation.length > 1 && 's'}
            </Text>

            <List className="text-natural-200">
              {accountsAwaitingConfirmation.map((account) => (
                <List.Item key={account.id}>
                  {account.name} {account.mask}
                  <AwaitingConfirmationButton
                    account={account}
                    setAccountToEdit={setAccountToEdit}
                    setInitialStateModalOpen={setInitialStateModalOpen}
                  />
                </List.Item>
              ))}
            </List>
          </Card>
        )}

        {sortedPlaidInstitutions.map((institution) => (
          <InstitutionCard
            key={institution.id}
            institution={institution}
            setAccountBanner={setAccountBanner}
            accountToEdit={accountToEdit}
            setAccountToEdit={setAccountToEdit}
            setInitialStateModalOpen={setInitialStateModalOpen}
            isOnboarding={isOnboarding}
          />
        ))}

        {accountToEdit && (
          <InitialStateDialog
            open={initialStateModalOpen}
            account={accountToEdit}
            onClose={() => {
              setInitialStateModalOpen(false)
              setAccountToEdit(null)
            }}
            setAccountBanner={setAccountBanner}
          />
        )}
      </div>
    )
  }

  if (isOnboarding) {
    return null
  }

  return (
    <div className="flex flex-col items-center">
      <Icon icon={regular('bank')} size="5x" color="gray" />
      <Text textAlign="center">
        You don&apos;t have any financial institution connections yet.
      </Text>
    </div>
  )
}
